import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import MarketingLayout from "@/components/MarketingLayout";
import { api } from "@/lib/api";

const PROVINCES = ["Alberta", "British Columbia", "Manitoba", "New Brunswick", "Newfoundland and Labrador", "Nova Scotia", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Other"];

export default function EarlyAccess() {
  const params = new URLSearchParams(window.location.search);
  const preselectedPlan = params.get("plan"); // "solo" | "founding" | "multi"
  const PLAN_LABEL = { solo: "Solo Shop ($49/mo)", founding: "Founding Dealer ($99/mo)", multi: "Multi-Location Pro ($249/mo)" };
  const [form, setForm] = useState({ name: "", dealership: "", email: "", city: "", province: "Ontario", num_locations: preselectedPlan === "multi" ? 3 : 1, current_system: "", approximate_inventory: "", interested_plan: preselectedPlan ? PLAN_LABEL[preselectedPlan] : "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const on = (k) => (e) => setForm({ ...form, [k]: k === "num_locations" ? Number(e.target.value) : e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/early-access", form); setDone(true); }
    catch (err) { toast.error(err?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  return (
    <MarketingLayout>
      <div className="tm-container py-16 max-w-2xl">
        <div className="tm-demo-tag mb-4">Early Access</div>
        <h1 className="text-3xl sm:text-4xl font-bold" data-testid="ea-heading">Request Early Access</h1>
        <p className="mt-3 text-[#3B424B]">Independent tire dealers only. We'll be in touch shortly to confirm plan details.</p>
        {preselectedPlan && PLAN_LABEL[preselectedPlan] && (
          <div className="mt-5 tm-card p-3 bg-white text-sm inline-flex items-center gap-2" data-testid="ea-preselected-plan">
            <span className="text-[#3B424B]">You picked:</span>
            <span className="font-semibold">{PLAN_LABEL[preselectedPlan]}</span>
          </div>
        )}
        {done ? (
          <div className="tm-card p-8 mt-8 flex items-start gap-4" data-testid="early-access-success">
            <CheckCircle2 className="text-[#14624A] shrink-0" />
            <div>
              <div className="font-semibold text-lg">You're on the list.</div>
              <p className="mt-2 text-sm text-[#3B424B]">We've received your request. A member of the TreadMargin team will reach out to discuss next steps and pricing details.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="tm-card p-6 sm:p-8 mt-8 space-y-4" data-testid="ea-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="tm-label">Your name *</label><input required className="tm-input" value={form.name} onChange={on("name")} data-testid="ea-name" /></div>
              <div><label className="tm-label">Dealership *</label><input required className="tm-input" value={form.dealership} onChange={on("dealership")} data-testid="ea-dealership" /></div>
              <div><label className="tm-label">Email *</label><input required type="email" className="tm-input" value={form.email} onChange={on("email")} data-testid="ea-email" /></div>
              <div><label className="tm-label">City *</label><input required className="tm-input" value={form.city} onChange={on("city")} data-testid="ea-city" /></div>
              <div>
                <label className="tm-label">Province/State *</label>
                <select className="tm-input" value={form.province} onChange={on("province")} data-testid="ea-province">
                  {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div><label className="tm-label">Number of locations</label><input type="number" min={1} className="tm-input" value={form.num_locations} onChange={on("num_locations")} data-testid="ea-locations" /></div>
              <div><label className="tm-label">Interested plan</label>
                <select className="tm-input" value={form.interested_plan} onChange={on("interested_plan")} data-testid="ea-plan">
                  <option value="">Not sure yet</option>
                  <option value="Solo Shop ($49/mo)">Solo Shop — $49/mo</option>
                  <option value="Founding Dealer ($99/mo)">Founding Dealer — $99/mo</option>
                  <option value="Multi-Location Pro ($249/mo)">Multi-Location Pro — $249/mo</option>
                </select>
              </div>
              <div><label className="tm-label">Current POS/shop-management (optional)</label><input className="tm-input" value={form.current_system} onChange={on("current_system")} data-testid="ea-system" /></div>
              <div><label className="tm-label">Approximate tire inventory (optional)</label><input className="tm-input" value={form.approximate_inventory} onChange={on("approximate_inventory")} placeholder="e.g. 800 tires" data-testid="ea-inventory" /></div>
            </div>
            <button type="submit" disabled={busy} className="tm-btn-gold w-full sm:w-auto" data-testid="ea-submit">{busy ? "Submitting…" : "Request Early Access"}</button>
          </form>
        )}
      </div>
    </MarketingLayout>
  );
}
